import { Fragment, useState, useMemo, useCallback, useEffect, useContext } from 'react';
import type { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import type { Translations } from '@aidkitorg/i18n/lib';
import type { DwollaData } from '@aidkitorg/types/lib/payments/rcTypes';
import { useLocalizedStrings } from "../Localization";
import { Input, Loading, MultiSelect, SelectOptionType, PaymentDisplay, paymentStatuses, buildSortedSelectOptions, Button, Tooltip, DataDisplayProvider, Switch } from '@aidkitorg/component-library';
import { usePost } from '../API';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { paymentToReadablePaymentMethod, paymentTypeToString } from '../utils/payment_utils';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { ConfigurationContext } from '../Context';

type ApiEntry = extractReturnAsJson<Directory['/payments/rc_actions']>['entries'][number];
type ExtraEntryDetails = {
  prettyName: string;
  prettyPaymentMethod: string;
  proposedDate: Date;
}
type Entry = ApiEntry & ExtraEntryDetails;
type PaymentMethodDetails = extractReturnAsJson<Directory['/payments/get_payment_method_details']>;

function isCancellable(entry: Entry, roles?: string[]) {
  // Only admins can cancel payments.
  if (!roles?.includes('admin')) {
    return false;
  }

  const actionInitiated = entry.dwolla_float_ach_grant_extra?.issued_transaction_id
    || entry.usio_mailed_grant_extra?.grant_source_sent_card_id
    || entry.usio_load_card_extra?.load_confirmation
    || entry.dynamic_metadata?.outcome
    || entry.dynamic_metadata?.load_id; // Givecard Load indicates a successful load by load_id, not outcome.

  // We can cancel when an action is proposed, scheduled, or if it is pending but we have not yet initiated the
  // action with the vendor.
  return entry.action_state === 'PROPOSED' || entry.action_state === 'SCHEDULED' || (entry.action_state === 'PENDING' && !actionInitiated);
}

function InstructionsTooltip(props: {
  instructions: string,
  side?: 'top' | 'bottom' | 'left' | 'right',
  align?: 'start' | 'center' | 'end',
}) {
  return <Tooltip side={props.side || 'top'} align={props.align || 'center'} text={<p className='text-sm max-w-[350px] mb-0'>{props.instructions}</p>}>
    <QuestionMarkCircleIcon className='w-5 h-5 text-gray-400 cursor-pointer' />
  </Tooltip>
}

function TopBanner(props: {
  applicantPaymentType?: string,
  totalPaidInCents: number,
}) {
  const L = useLocalizedStrings();

  const totalPaidInDollars = (props.totalPaidInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  return (
    <div className="w-full pr-2 py-4 pr-4 flex flex-col md:flex-row justify-between items-center border-b-4 border-indigo-600">
      <div className="flex flex-col px-2 items-center md:items-start md:mb-0">
        <span className="text-sm text-gray-600 flex items-center gap-1">
          {L.payments.total_paid}
          <InstructionsTooltip instructions={L.payments.total_paid_explanation} side='right' />
        </span>
        <h3 className="pt-2">{totalPaidInDollars}</h3>
      </div>
      <div className="flex flex-col items-center md:items-end">
        <span className="text-sm text-gray-600">{L.payments.current_payment_method}</span>
        <h3 className="pt-2">{paymentToReadablePaymentMethod(props.applicantPaymentType || '', L)}</h3>
      </div>
    </div>
  );
}

function PaymentMethodDetails(props: {
  paymentMethod: string,
  applicantInfo: Record<string, string | undefined> | undefined,
  details: PaymentMethodDetails | undefined,
}) {
  const L = useLocalizedStrings();

  if (!props.details || !props.applicantInfo) {
    return <Loading loading />;
  }

  const applicantInfo = props.applicantInfo;

  return <>
    <h5>{paymentToReadablePaymentMethod(props.paymentMethod, L)}</h5>
    {props.details.map((detail) =>
      <PaymentMethodDetail detail={detail} applicantInfo={applicantInfo} />
    )}
  </>
}

function PaymentMethodDetail(props: {
  detail: PaymentMethodDetails[number],
  applicantInfo: Record<string, string | undefined>,
}) {
  const { detail, applicantInfo } = props;
  const L = useLocalizedStrings();

  const isLink = detail.value.startsWith('https://') || detail.value.startsWith('http://');

  const label = useMemo(() => {
    let label = L.payments.card_details[detail.key];

    if (detail.keyReplacements) {
      Object.entries(detail.keyReplacements).forEach(([replace, replaceWith]) => {
        label = label.replace(replace, replaceWith);
      });
    }

    return label.trim();
  }, [detail, L]);

  const value = useMemo(() => {
    const lines = detail.value.split('\n');
    return <span>{lines.map((line, i) => <Fragment key={i}>{line}<br /></Fragment>)}</span>;
  }, [detail.value]);

  const valueExplanation = useMemo(() => {
    const bankNames = [
      'bancorp',
      'sutton',
      'lincoln savings'
    ];

    if (detail.key === 'bank_name' && bankNames.some(name => 
      detail.value.toLowerCase().includes(name)
    )) {
      return L.payments.card_details.bank_explanation;
    }

    return null;
  }, [detail.key, detail.value, L]);

  const matchingApplicantInfo = detail.matchingApplicantInfoKey ? applicantInfo[detail.matchingApplicantInfoKey] : null;

  return <div className='mt-3'>
    {isLink
      ? <a className='font-semibold' href={detail.value} target='_blank' rel='noopener noreferrer'>{label}</a>
      : <>
        <div className='flex items-center gap-1'><span className='font-semibold'>{label}</span>{detail.key === 'intake_verification_status' && <InstructionsTooltip instructions={L.payments.card_details.givecard_intake_explanation} side='bottom' align='end' />}</div>
        <div>{value}</div>
        {valueExplanation && <div className='text-sm text-gray-500'>{valueExplanation}</div>}
      </>
    }
    {!!detail.matchingApplicantInfoKey &&
      detail.value !== matchingApplicantInfo &&
      <div className='rounded-lg py-1 px-2 bg-yellow-100 text-yellow-80 text-ellipsis overflow-hidden'>
        <span>
          {L.payments.applicant_info_does_not_match
            .replace('$key', detail.matchingApplicantInfoKey)
            .replace('$value', matchingApplicantInfo ? `"${matchingApplicantInfo}"` : L.payments.applicant_info_not_set)
          }
        </span>
      </div>
    }
  </div>;
}

function PaymentMethodSettings(props: { applicant: string }) {
  const L = useLocalizedStrings();

  const [paymentsPaused, setPaymentsPaused] = useState<boolean>(false);
  const [isEditingPausedSettings, setIsEditingPausedSettings] = useState<boolean>(false);

  const fetchCurrentSettings = usePost('/payments/blocked_payments');
  const saveSettings = usePost('/payments/update_blocked_payments');

  const updatePausedPayments = useCallback(async (newState: boolean) => {
    saveSettings({
      applicant: props.applicant,
      updatedSettings: {
        blockedPayments: newState.toString()
      }
    });
    setPaymentsPaused(newState);
    setIsEditingPausedSettings(false);
  }, [props.applicant, saveSettings]);

  useEffect(() => {
    (async () => {
      const settings : any = await fetchCurrentSettings({
        applicant: props.applicant
      });
      if (settings?.value && settings.value.length > 0) {
        setPaymentsPaused(settings.value[0].value === 'true');
      }
    })();
  }, []);

  return <div>
    <h5>{L.payments.settings}</h5>

    <div className={`flex items-center gap-2 mt-2 text-sm rounded p-2 ${paymentsPaused ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'}`}>
      {paymentsPaused ? (
        <XCircleIcon className={`w-6 h-6`} />
      ) : (
        <CheckCircleIcon className={`w-6 h-6`} />
      )}
      <span>{paymentsPaused ? L.payments.payments_paused : L.payments.payments_enabled}</span>
    </div>
    <div className='mt-2 text-sm flex items-start justify-between'>
      <div className='flex items-center gap-2 mt-3 mb-1'>
        <Button variant='secondary' size='sm' disabled={isEditingPausedSettings} onClick={() => setIsEditingPausedSettings(!isEditingPausedSettings)}>{paymentsPaused ? L.payments.re_enable_payments : L.payments.pause_payments}</Button>
        <InstructionsTooltip instructions={L.payments.pause_payments_instructions} side='bottom' />
      </div>
      {isEditingPausedSettings &&
        <div>
          <span>{L.payments.are_you_sure}</span>
          <div className='flex items-center gap-2'>
            <Button variant='secondary' size='sm' onClick={() => updatePausedPayments(!paymentsPaused)}>{L.payments.yes}</Button>
            <Button variant='secondary' size='sm' onClick={() => setIsEditingPausedSettings(!isEditingPausedSettings)}>{L.payments.no}</Button>
          </div>
        </div>
      }
    </div>
  </div>
}

const paymentStatusOptions = buildSortedSelectOptions(paymentStatuses, (status) => `${status[0]}${status.slice(1).toLowerCase()}`);

export function ApplicantPaymentPage({ applicant }: { applicant: string }) {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);
  
  const getEverything = usePost('/applicant/get_everything');
  const getPayments = usePost('/payments/rc_actions');
  const getPaymentMethodDetails = usePost('/payments/get_payment_method_details');

  const [entries, setEntries] = useState<Entry[] | undefined>();
  const [info, setInfo] = useState<Record<string, string | undefined> | undefined>();
  const [paymentMethodDetails, setPaymentMethodDetails] = useState<PaymentMethodDetails | undefined>();
  const [filterText, setFilterText] = useState('');
  const [allPaymentMethods, setAllPaymentMethods] = useState<SelectOptionType[]>([]);
  const [paymentMethodFilters, setPaymentMethodFilters] = useState<SelectOptionType[] | undefined>();
  const [paymentStatusFilters, setPaymentStatusFilters] = useState<SelectOptionType[]>(paymentStatusOptions);

  const [useUTC, setUseUTC] = useState(false);
  const onCheckedChange = useCallback((checked: boolean) => setUseUTC(checked), []);

  const totalPaidInCents = useMemo(() => {
    let total = 0;
    entries?.forEach(({ action_state, amount, return_entry }) => {
      if (action_state === 'COMPLETE' || action_state === 'PENDING') {
        total += +amount;
        if (return_entry) {
          total += +return_entry.amount;
        }
      }
    });
    return total;
  }, [entries]);

  const refreshEntries = useCallback(async (signal?: AbortSignal) => {
    const payments = await getPayments({ applicant }, signal);

    if (payments) {
      let { entries } = payments;

      const sortEntries = (a: Entry, b: Entry) => b.proposedDate.getTime() - a.proposedDate.getTime();

      const sortedEntries = entries
        .map((entry) => ({
          ...entry,
          prettyName: getPaymentName(entry.name, entry.action_target_field, entry.action_attempt_number, L),
          prettyPaymentMethod: paymentTypeToString(entry.action_kind || '', parseFloat(entry.amount) < 0),
          proposedDate: new Date(entry.proposed_date),
        }))
        .sort(sortEntries);

      const allPaymentMethods = new Set(sortedEntries.map(({ prettyPaymentMethod }) => prettyPaymentMethod));
      const allPaymentMethodOptions = buildSortedSelectOptions(Array.from(allPaymentMethods));

      setEntries(sortedEntries);
      setAllPaymentMethods(allPaymentMethodOptions);
    }
  }, [applicant]);

  const filteredEntries = useMemo(() => {
    const paymentMethods = paymentMethodFilters?.map(({ value }) => value);
    const paymentStatuses = paymentStatusFilters.map(({ value }) => value);

    return entries?.filter((entry) => {
      const filterTextMatches = entry.prettyName.toLowerCase().includes(filterText.toLowerCase());
      const paymentMethodMatches = !paymentMethods || paymentMethods.includes(entry.prettyPaymentMethod);
      const paymentStatusMatches = paymentStatuses.includes(entry.action_state);
      return filterTextMatches && paymentMethodMatches && paymentStatusMatches;
    });
  }, [entries, filterText, paymentMethodFilters, paymentStatusFilters]);

  const latestAttempts = useMemo(() => {
    const latest: Record<string, Date> = {};
    entries?.forEach((entry) => {
      if (entry.action_target_field) {
        const proposedAt = entry.proposedDate;
        if (!latest[entry.action_target_field] || (proposedAt && proposedAt.getTime() > latest[entry.action_target_field].getTime())) {
          latest[entry.action_target_field] = proposedAt;
        }
      }
    });
    return latest;
  }, [entries]);

  useAsyncEffect(async (signal) => {
    setEntries(undefined);
    await refreshEntries(signal);
  }, [applicant]);

  useAsyncEffect(async () => {
    const info = (await getEverything({ uid: applicant }))?.info || {};
    setInfo(info);
  }, [applicant]);

  const currentPaymentMethod = useMemo(() => {
    return info?.['applicant_payment_type'] || entries?.[entries.length - 1]?.action_kind || undefined;
  }, [info, entries]);

  useAsyncEffect(async () => {
    setPaymentMethodDetails(undefined);

    if (currentPaymentMethod) {
      const paymentMethodDetails = await getPaymentMethodDetails({ applicant, paymentMethod: currentPaymentMethod, cardId: info?.['card_id'] || '' });
      setPaymentMethodDetails(paymentMethodDetails);
    }
  }, [applicant, currentPaymentMethod, info]);

  const resolvePaymentFailure = usePost('/payments/resolve_payment_failure');
  const resolveFailureCallback = useCallback(async (targetField: string) => {
    await resolvePaymentFailure({ applicant, paymentTargetField: targetField || ''});

    // refresh the info - this will show us the failure as resolved.
    const info = (await getEverything({ uid: applicant }))?.info || {};
    setInfo(info);
  }, [applicant, resolvePaymentFailure]);

  const cancelPayment = usePost('/payments/cancel_rc_action');
  const cancelCallback = useCallback(async (kind: string, actionID: string) => {
    await cancelPayment({ kind, actionID });

    // refresh entries to show the result of the cancellation.
    await refreshEntries();
  }, [cancelPayment]);

  if (!filteredEntries) {
    return <Loading loading />;
  }

  return <div className='flex flex-col px-2 min-w-[620px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
    <TopBanner
      applicantPaymentType={currentPaymentMethod}
      totalPaidInCents={totalPaidInCents}
    />
    <div className="flex flex-col md:flex-row-reverse md:gap-x-8 justify-around py-6">
      {currentPaymentMethod && <div className="w-full md:w-2/5 flex flex-col gap-4">
        <div className="p-4 rounded-lg border border-gray-300">
          <PaymentMethodDetails
            paymentMethod={currentPaymentMethod}
            applicantInfo={info}
            details={paymentMethodDetails}
          />
        </div>
        {config.roles?.includes('admin') && <div className="p-4 rounded-lg border border-gray-300">
          <PaymentMethodSettings
            applicant={applicant}
          />
        </div>}
      </div>}
      <div className="flex flex-col gap-2 w-full md:w-3/5 mt-6 md:mt-0">
        {!!entries && !!entries.length && <>
          <div>
            <Input
              type="text"
              label={L.component_library.data_table.search}
              showLabel={false}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={L.component_library.data_table.search}
              className="p-2 h-full border border-gray-300 rounded-md text-sm w-full"
            />
            {config.roles?.includes('admin') && (config.user?.email.endsWith('@aidkit.cloud') || config.user?.email.endsWith('@aidkit.org')) &&
              <span className='text-xs text-gray-500'>{L.payments.not_seeing_payment} <a href="/test-eligibility" target="_blank" rel="noopener noreferrer">{L.payments.test_eligibility_link}</a></span>
            }
          </div>
          <div className='flex justify-evenly gap-1'>
            <div className='flex-1 min-w-0'>
              <MultiSelect
                description={L.payments.payment_method_filter_help_text}
                label={L.payments.payment_method_filter_label}
                labelLocation='above'
                value={paymentMethodFilters || allPaymentMethods}
                options={allPaymentMethods}
                onChange={(value) => setPaymentMethodFilters(value)}
              />
            </div>
            <div className='flex-1 min-w-0'>
              <MultiSelect
                description={L.payments.payment_status_filter_help_text}
                label={L.payments.payment_status_filter_label}
                labelLocation='above'
                value={paymentStatusFilters}
                options={paymentStatusOptions}
                onChange={(value) => setPaymentStatusFilters(value)}
              />
            </div>
          </div>
        </>}
        {filteredEntries.length
          ? <DataDisplayProvider value={{ displayInUTC: useUTC }}>
            <div className='flex justify-end'>
              <Switch label="Display time in UTC" onCheckedChange={onCheckedChange} />
            </div>
            <div className='flex flex-col gap-4'>
              {filteredEntries.map((entry, index) =>
                <ApplicantPayment
                  key={entry.action_id}
                  entry={entry}
                  actionRequired={!!entry.action_target_field
                    && latestAttempts[entry.action_target_field] === entry.proposedDate
                    && !!info?.[entry.action_target_field + '_failure']
                    && (info?.['stage'] === 'Correction Requested' || !!info?.[entry.action_target_field])
                  }
                  resolveFailureCallback={resolveFailureCallback}
                  cancelCallback={cancelCallback}
                  paymentIndex={index}
                />
              )}
            </div>
          </DataDisplayProvider>
          : <p className='text-center mt-5'>{L.payments.no_payments}</p>
        }
      </div>
    </div>
  </div>;
}

function getPaymentName(name: string | undefined, targetField: string | undefined, attempt: number, L: Translations) {
  const nameToUse = name ?? prettifyTargetField(targetField, L);

  if (attempt > 1) {
    const attemptText = L.payments.attempt.replace('$n', attempt.toString());
    return `${nameToUse} ${attemptText}`;
  } else {
    return nameToUse;
  }
}

function prettifyTargetField(targetField: string | undefined, L: Translations) {
  if (!targetField) {
    return L.applicant.payment;
  }

  return targetField
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

function ApplicantPayment({ entry, actionRequired, resolveFailureCallback, cancelCallback, paymentIndex }: {
  entry: Entry,
  actionRequired: boolean,
  resolveFailureCallback: (targetField: string) => Promise<void>,
  cancelCallback: (kind: string, actionID: string) => Promise<void>,
  paymentIndex: number
}) {
  const L = useLocalizedStrings();
  const config = useContext(ConfigurationContext);

  if (entry.action_state === 'ANTICIPATED') {
    return null;
  }

  const usioLoadConfirmation = entry.usio_load_card_extra?.load_confirmation;
  const dwollaTraceId = (entry.dwolla_float_ach_grant_extra?.dwolla_data as DwollaData)?.achDetails?.destination?.traceId;
  const rtpTraceId = entry.transaction_dynamic_metadata?.traceId;
  const rtpExternalId = entry.transaction_dynamic_metadata?.recipient?.externalId;
  
  const ledger = entry.action_reference?.split(':')?.[0]?.split('--')?.[1];
  const loadedToCardId = entry.usio_load_card_extra?.card_id || entry.dynamic_metadata?.cardId || entry.dynamic_metadata?.toCardId;
  const issuedCardId = entry.usio_mailed_grant_extra?.grant_source_sent_card_id // usio mailed cards
    || entry.dynamic_metadata?.issued_card // givecard mailed cards
    || entry.dynamic_metadata?.outcome?.cardId; // usbank cards and givecard virtual cards

  const isAKAdmin = config.roles?.includes('admin') && (config.user?.email.endsWith('@aidkit.cloud') || config.user?.email.endsWith('@aidkit.org'));
  const isPaymentAdmin = config.roles?.includes('admin') || config.roles?.includes('program_admin');

  return <PaymentDisplay
    paymentConfig={{
      id: entry.action_id,
      paymentType: entry.prettyPaymentMethod,
      proposedAt: entry.proposedDate,
      approvedAt: entry.approval_date ? new Date(entry.approval_date) : undefined,
      completedAt: entry.issued_date ? new Date(entry.issued_date) : undefined,
      failedAt: entry.failure_date ? new Date(entry.failure_date) : undefined,
      scheduledFor: entry.scheduled_for ? new Date(entry.scheduled_for) : undefined,
      cardIssued: issuedCardId,
      vendorSpecificMetadata: {
        // ...(usioLoadConfirmation && { [L.payments.usio_load_confirmation]: usioLoadConfirmation }),
        ...(dwollaTraceId && { [L.payments.trace_id]: dwollaTraceId }),
        ...(rtpTraceId && { [L.payments.trace_id]: rtpTraceId }),
        ...(rtpExternalId && { [L.payments.rtp_external_id]: rtpExternalId }),
        ...(entry.dynamic_metadata?.routingNumber && { [L.payments.routing_number]: entry.dynamic_metadata?.routingNumber }),
        // ...(loadedToCardId && { [L.payments.card_loaded]: loadedToCardId }),
        ...(entry.dynamic_metadata?.outcome?.estimatedDeliveryDate && { [L.payments.estimated_delivery_date]: new Date(entry.dynamic_metadata?.outcome?.estimatedDeliveryDate) }),
        ...(isAKAdmin && entry.action_state === 'PENDING' && dwollaTraceId && { 'Note to AK staff': 'Payment is still PENDING in database due to ACH 14 day buffer.' }),
      },
      status: (entry.action_state === 'PENDING' && dwollaTraceId) ? 'COMPLETE' : entry.action_state,
      paymentName: entry.prettyName,
      returnEntry: entry.return_entry,
      failure: entry.failure_reason ? { description: entry.failure_reason } : undefined,
      amountInCents: +entry.amount,
      actionRequired: actionRequired,
      ...(actionRequired && isPaymentAdmin && {
        markResolved: async () => {
          await resolveFailureCallback(entry.action_target_field || '');
        }
      }),
      ...(entry.action_kind && isCancellable(entry, config.roles) ? {
        cancel: async () => {
          await cancelCallback(entry.action_kind!, entry.action_id);
        }
      } : {}),
      ...(isAKAdmin && { ledger }),
    }}
  />;
}
